'use client';

import { useState, useRef, useLayoutEffect, memo, type FC, useCallback } from 'react';
import { SkinImage } from '../ui/SkinImage';
import { useNavigation } from '../../contexts/NavigationContext';
import { useTopRecentDrops, useRecentDrops } from '../../queries/useHomeQueries';
import { useTopDropsSubscription } from '../../hooks/useTopDropsSubscription';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { cn } from '../../utils/cn';
import { rarityGradient } from '../../utils/rarityGradient';
import { parseItemDetails } from '../../utils/formatters';
import { createTranslations } from '../../i18n/types';
import type { RecentInventoryItem } from '../../services/generated/Api';

const translations = createTranslations({
  en: { top: 'Top', live: 'Live' },
  ru: { top: 'Топ', live: 'Live' },
  uz: { top: 'Top', live: 'Live' },
});

type DropMode = 'top' | 'recent';

/** Compact drop tile — image + rarity glow + skin name */
const DropTile: FC<{
  drop: RecentInventoryItem;
  onClick: () => void;
}> = memo(({ drop, onClick }) => {
  const { skinName } = parseItemDetails(drop.item.name);

  return (
    <button
      className="relative w-full h-full rounded-xl overflow-hidden border border-white/[0.06] cursor-pointer bg-black/40 p-0 outline-none"
      onClick={onClick}
    >
      {/* Rarity gradient — same as ItemCard */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: rarityGradient(drop.item.rarityColor) }}
      />
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
      />
      {/* Item image */}
      <SkinImage
        name={drop.item.name}
        src={drop.item.imageUrl}
        className="absolute inset-0"
        imgClassName="w-full h-full object-contain p-2 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
        priority
      />
      {/* Rank removed */}
      {/* Skin name overlay */}
      <div className="absolute bottom-1 left-0 right-0 flex justify-center pointer-events-none">
        <span className="text-[9px] font-medium text-white/70 truncate max-w-[90%] px-1">{skinName || drop.item.name}</span>
      </div>
    </button>
  );
});

export const DesktopDropsFeed: FC = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const { data: topDrops = [] } = useTopRecentDrops();
  const { data: recentDrops = [] } = useRecentDrops();
  const [mode, setMode] = useState<DropMode>('recent');
  const previousDropIdsRef = useRef<Set<string>>(new Set());
  const isFirstRenderRef = useRef(true);
  const innerRef = useRef<HTMLDivElement>(null);

  useTopDropsSubscription();

  const drops = mode === 'top' ? topDrops : recentDrops;

  // Track recent drop IDs separately from mode
  useLayoutEffect(() => {
    if (recentDrops.length > 0 && previousDropIdsRef.current.size === 0) {
      previousDropIdsRef.current = new Set(recentDrops.map(d => d.id));
      isFirstRenderRef.current = false;
    }
  }, [recentDrops]);

  // Animate new recent drops sliding in from left (only in 'recent' mode)
  useLayoutEffect(() => {
    if (!innerRef.current || mode !== 'recent' || recentDrops.length === 0) {
      if (innerRef.current && mode !== 'recent') {
        innerRef.current.style.transition = 'none';
        innerRef.current.style.transform = '';
      }
      return;
    }

    if (isFirstRenderRef.current) return;

    const currentIds = new Set(recentDrops.map(d => d.id));

    let newCount = 0;
    currentIds.forEach(id => {
      if (!previousDropIdsRef.current.has(id)) newCount++;
    });

    if (newCount > 0) {
      const el = innerRef.current;
      const firstChild = el.firstElementChild as HTMLElement | null;
      const itemWidth = firstChild ? firstChild.offsetWidth + 6 : 96;
      const newOffset = newCount * itemWidth;

      const computedStyle = window.getComputedStyle(el);
      const matrix = new DOMMatrix(computedStyle.transform);
      const currentX = matrix.m41;
      const startX = currentX - newOffset;

      el.style.transition = 'none';
      el.style.transform = `translateX(${startX}px)`;
      el.offsetHeight;
      el.style.transition = 'transform 0.5s ease-out';
      el.style.transform = 'translateX(0)';
    }

    previousDropIdsRef.current = currentIds;
  }, [recentDrops, mode]);

  const handleDropClick = useCallback(
    (itemId: string) => {
      navigation.pushScreen(navigation.state.activeTab, 'inventory-item', { itemId });
    },
    [navigation],
  );

  if (topDrops.length === 0 && recentDrops.length === 0) return null;

  return (
    <div className="w-full shrink-0 flex items-center border-b border-white/[0.04] overflow-hidden">
      {/* Toggle */}
      <div className="flex flex-col shrink-0 self-stretch border-r border-white/[0.06]">
        <button
          className={cn(
            'flex-1 px-3 text-[10px] font-bold uppercase tracking-widest border-none cursor-pointer transition-all',
            mode === 'recent' ? 'text-green-400' : 'text-white/20 hover:text-white/35 bg-transparent',
          )}
          onClick={() => setMode('recent')}
        >
          {t('live')}
        </button>
        <button
          className={cn(
            'flex-1 px-3 text-[10px] font-bold uppercase tracking-widest border-none cursor-pointer transition-all',
            mode === 'top' ? 'text-amber-400' : 'text-white/20 hover:text-white/35 bg-transparent',
          )}
          onClick={() => setMode('top')}
        >
          {t('top')}
        </button>
      </div>

      {/* Horizontal scroll */}
      <div className="flex-1 min-w-0 relative">
        {/* Left fade */}
        <div className="absolute top-0 bottom-0 left-0 w-8 bg-gradient-to-r from-[#07090D] to-transparent z-10 pointer-events-none" />
        {/* Right fade */}
        <div className="absolute top-0 bottom-0 right-0 w-8 bg-gradient-to-l from-[#07090D] to-transparent z-10 pointer-events-none" />
        <div className="overflow-x-auto overflow-y-hidden [scrollbar-width:none] py-1 px-2">
          <div ref={innerRef} className="flex gap-0.5">
            {drops.map((drop) => (
              <div key={drop.id} className="w-[90px] h-[90px] shrink-0">
                <DropTile
                  drop={drop}
                  onClick={() => handleDropClick(drop.id)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
